/**
 * Multi-Hierarchy Details table — persisted view configuration.
 *
 * The Details tab lists the rows of the report's lens that sit under the report's row hierarchy,
 * with the hierarchy path, the chart-of-accounts columns and the lens columns side by side. This
 * module holds only the part that is *saved with the report* (and therefore travels with a saved
 * view). The request/response contract for fetching the table lives in `../detailsTypes`, which
 * imports from here — the dependency runs one way.
 *
 * Anything added here must also be registered in `multiHierarchyDetailsRecord` below AND reach
 * `multiHierarchyAccountSummaryRecord` in `utils/baseUtils`, or `createRecord` silently discards
 * it: the setting applies live, works, and vanishes on reload.
 */
import { createRecord, DeepPartial } from '../record';

/**
 * Column key of a group row's child count. Group rows are keyed by the grouped column's own key,
 * so this only needs to be distinct from every column the table can show.
 */
export const MH_DETAILS_GROUP_CHILD_COUNT_KEY = 'og_child_count';

/**
 * Stands in for a `null` group value on the wire. AG Grid's server-side group keys must be strings,
 * so a group of rows whose grouped column is empty travels as this sentinel and is read back as
 * `IS NULL` by the pipeline.
 */
export const MH_DETAILS_BLANK_GROUP_KEY = '__og_blank__';

/**
 * Most rows a CSV export writes. The service enforces it and the app warns above it, so both read it
 * from here. Matches `EXPORT_ROW_LIMIT` in reporting-app and `MAX_TOTAL_RESULT_LIMIT` in queryRunning.
 */
export const MH_DETAILS_EXPORT_ROW_LIMIT = 50000;

export type MhDetailsFilterOperator =
  | 'contains'
  | 'notContains'
  | 'equals'
  | 'notEquals'
  | 'startsWith'
  | 'endsWith'
  | 'blank'
  | 'notBlank'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'inRange'
  | 'in';

/**
 * One cell of the Details table.
 *
 * Text, date (ISO `YYYY-MM-DD`) and boolean (`"true"` / `"false"`) columns carry strings, numeric
 * columns numbers, and any cell can be null. Declared here, with the persisted filter types that
 * compare against it, rather than in `detailsTypes` — that module imports this one, so the reverse
 * would be a cycle.
 */
export type MhDetailsCell = string | number | null;

export interface MhDetailsFilter {
  columnKey: string;
  operator: MhDetailsFilterOperator;
  /** Date columns take an ISO `YYYY-MM-DD` string; numeric columns a number. */
  value?: MhDetailsCell;
  /** Upper bound for `inRange`. */
  valueTo?: MhDetailsCell;
  /**
   * Value set for `in`.
   *
   * Mutable on purpose: this type is also the persisted shape, and immer's `WritableDraft` cannot
   * accept a readonly array. The decoded wire payload is copied into this shape at the pipeline
   * boundary instead.
   */
  values?: MhDetailsCell[];
}

export type MhDetailsSortDirection = 'asc' | 'desc';

export interface MhDetailsSort {
  columnKey: string;
  direction: MhDetailsSortDirection;
}

export interface MhDetailsColumnConfig {
  columnKey: string;
  hidden?: boolean;
  pinned?: 'left' | 'right';
  /** Display position. Absent means "wherever the server put it". */
  order?: number;
}

/**
 * Group By. One level is honoured today; the array keeps room for nesting without a
 * persisted-shape change.
 */
export interface MhDetailsGroupBy {
  columnKeys: string[];
}

/**
 * Saved with the MH report configuration, so it round-trips through saved views the same way row
 * groups and column groups do.
 *
 * Deliberately NOT stored on `IReportConfiguration.detailTableQuery`: that field is populated on MH
 * configs by a flag-gated backfill on read (`addNewlyAddedColumnsToDetailsTable`, gated on
 * `product-data-platform-feature-nextgen-mutable-standard-dataset`) keyed on the report's single
 * legacy `report.lensId` rather than the MH column groups' lenses, so anything stored there is
 * liable to be rewritten by an unrelated mechanism.
 */
export interface MhDetailsViewConfig {
  columns?: MhDetailsColumnConfig[];
  filters?: MhDetailsFilter[];
  sort?: MhDetailsSort[];
  showTotals?: boolean;
  groupBy?: MhDetailsGroupBy;
  /** Group keys (the grouped column's values, blank as `MH_DETAILS_BLANK_GROUP_KEY`) left expanded. */
  expandedGroupKeys?: string[];
  /** Which column group's lens the table lists. Absent means the first data column group. */
  columnGroupId?: string;
}

const multiHierarchyDetailsRecord = createRecord<MhDetailsViewConfig>({
  columns: undefined,
  filters: undefined,
  sort: undefined,
  showTotals: undefined,
  groupBy: undefined,
  expandedGroupKeys: undefined,
  columnGroupId: undefined
});

/**
 * `undefined` defaults allow-list each key while keeping it out of the saved JSON
 * (`JSON.stringify` drops `undefined`), so an untouched Details tab adds nothing to the
 * persisted configuration.
 */
export const createMhDetailsViewConfig = (values: DeepPartial<MhDetailsViewConfig> = {}): MhDetailsViewConfig =>
  multiHierarchyDetailsRecord(values);
